import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useEditor } from "./store";
import { PreviewPane } from "./PreviewPane";
import { ControlBar } from "./ControlBar";
import { Timeline } from "./Timeline";
import { AudioMeter } from "./AudioMeter";
import TimelineElementEditor from "./TimelineElementEditor";
import { UserRole } from "@/contexts/UserContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/shadcn/dropdown-menu";
import { FiMoreHorizontal, FiEdit2, FiRotateCcw } from "react-icons/fi";
import { FaChevronLeft } from "react-icons/fa";
import { Link } from 'react-router-dom'
import { LuAudioLines, LuVideo, LuLoaderCircle, LuLetterText } from "react-icons/lu";
import { exportTimeline, exportAudio, downloadBlob, isExportSupported } from "./export";
import { useShow } from "@/contexts/ShowContext";
import Loader from "@/components/loader";
import Button from "../ui/Button";
import { CiSaveUp1, CiViewTimeline } from "react-icons/ci";
import { useNavigate, useSearchParams } from "react-router-dom";
import TimelineAssets from "./TimelineAssets";
import StoryboardGenerations from "@/components/storyboard/StoryboardGenerations";
import TimelineComments, { useTimelineComments } from "./TimelineComments";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/shadcn/resizable";
import { useModal } from "@/contexts/ModalContext";
import ConfirmModal from "@/components/modals/ConfirmModal";
import { DragPreviewProvider } from "@/components/ui/DragPreview";
import { SketchEditor } from "@/components/SketchEditor";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/shadcn/tabs";

dayjs.extend(relativeTime);



export default function TimelineEditor({ timelineId: timelineIdParam }) {
  const { show } = useShow();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hcParam = searchParams.get('hc');
  const toJSON = useEditor((s) => s.toJSON);
  const loadFromDB = useEditor((s) => s.loadFromDB);
  const saveToDB = useEditor((s) => s.saveToDB);
  const isLoading = useEditor((s) => s.isLoading);
  const isSaving = useEditor((s) => s.isSaving);
  const isDirty = useEditor((s) => s.isDirty);
  const lastSavedAt = useEditor((s) => s.lastSavedAt);
  const timelineId = useEditor((s) => s.timelineId);
  const timelineName = useEditor((s) => s.timelineName);
  const setTimelineName = useEditor((s) => s.setTimelineName);
  const selectedClipIds = useEditor((s) => s.selectedClipIds);
  const clips = useEditor((s) => s.clips);
  const assets = useEditor((s) => s.assets);
  const episodeId = useEditor((s) => s.episodeId);
  const segmentId = useEditor((s) => s.segmentId);
  const sketchEditorClipId = useEditor((s) => s.sketchEditorClipId);
  const closeSketchEditor = useEditor((s) => s.closeSketchEditor);
  const handleSketchSaveComplete = useEditor((s) => s.handleSketchSaveComplete);
  const { openModal } = useModal();
  const [loadError, setLoadError] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [generationsOpen, setGenerationsOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(!!hcParam);
  const [highlightCommentId, setHighlightCommentId] = useState(hcParam);
  const [, setTick] = useState(0);
  const effectiveTimelineId = timelineId || timelineIdParam;
  const { comments: timelineComments, loading: commentsLoading, fetchComments, addComment, updateComment } = useTimelineComments(effectiveTimelineId);
  const [editorTab, setEditorTab] = useState('assets');

  const setPlayhead = useEditor((s) => s.setPlayhead);

  const handleCommentMarkerClick = useCallback((commentId) => {
    setCommentsOpen(true);
    setHighlightCommentId(commentId);
  }, []);

  useEffect(() => {
    if (!hcParam || isLoading || commentsLoading || !timelineComments.length) return;
    const comment = timelineComments.find((c) => c._id === hcParam);
    if (comment?.timestamp != null) {
      setPlayhead(comment.timestamp);
    }
  }, [hcParam, isLoading, commentsLoading, timelineComments, setPlayhead]);

  const selectedClipPreview = useMemo(() => {
    if (selectedClipIds.length !== 1) return null;
    const clip = clips.find((c) => c.id === selectedClipIds[0]);
    if (!clip) return null;
    const asset = assets[clip.assetId];
    if (!asset?.url) return null;
    const type = asset.mediaType || asset.type;
    if (!['image', 'video', 'audio'].includes(type)) return null;
    return { url: asset.thumbnailUrl || asset.url, type };
  }, [selectedClipIds, clips, assets]);

  const sketchEditorProps = useMemo(() => {
    if (!sketchEditorClipId) return null;
    const clip = clips.find((c) => c.id === sketchEditorClipId);
    if (!clip) return null;
    const asset = clip.assetId ? assets[clip.assetId] : null;
    const existingSketchId = asset?.sketchId || null;
    const initialImageUrl = (!existingSketchId && asset?.url) ? asset.url : null;

    const onionImages = clips
      .filter((c) => c.trackId === clip.trackId && c.start < clip.start && assets[c.assetId]?.sketchId)
      .sort((a, b) => b.start - a.start)
      .slice(0, 10)
      .map((c) => {
        const a = assets[c.assetId];
        return a?.thumbnailUrl || a?.url || null;
      })
      .filter(Boolean)
      .reverse();

    return { sketchId: existingSketchId, initialImageUrl, onionImages };
  }, [sketchEditorClipId, clips, assets]);

  useEffect(() => {
    if (!isDirty || !lastSavedAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [isDirty, lastSavedAt]);
  const renameInputRef = useRef(null);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const pendingNavRef = useRef(null);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  // Ctrl/Cmd+S - save timeline
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (isDirtyRef.current) saveToDB();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [saveToDB]);

  // Warn on browser refresh / tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleUnsavedNav = useCallback(async (navigate) => {
    const result = await openModal(
      <ConfirmModal
        title="Unsaved Changes"
        message="You have unsaved changes. What would you like to do?"
        type="warning"
        buttons={[
          { label: 'Discard Changes', type: 'secondary', onClick: () => {} },
          { label: 'Save Changes', type: 'primary', onClick: saveToDB },
        ]}
      />
    );
    if (result !== undefined) {
      useEditor.setState({ isDirty: false });
      navigate();
    }
  }, [openModal, saveToDB]);

  // Intercept in-app link clicks
  useEffect(() => {
    const handleClick = (e) => {
      if (!isDirtyRef.current) return;
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("blob:") || anchor.target === "_blank" || anchor.hasAttribute("download")) return;

      // anchor href already includes router basename (e.g. /kagami); strip it so navigate() doesn't prepend it again
      const basename = import.meta.env.BASE_URL.replace(/\/$/, "");
      const navTarget = basename && href.startsWith(basename + "/") ? href.slice(basename.length) : href;

      e.preventDefault();
      e.stopPropagation();
      pendingNavRef.current = navTarget;

      handleUnsavedNav(() => {
        navigate(pendingNavRef.current);
      }).finally(() => {
        pendingNavRef.current = null;
      });
    };
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [openModal, navigate, handleUnsavedNav]);

  // Intercept browser back/forward buttons
  useEffect(() => {
    if (!isDirty) return;

    history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      if (!isDirtyRef.current) return;

      history.pushState(null, "", window.location.href);

      handleUnsavedNav(() => {
        history.go(-2);
      });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty, openModal, handleUnsavedNav]);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState("");
  const [exportError, setExportError] = useState(null);

  const handleExportVideo = useCallback(async () => {
    if (isExporting) return;
    
    if (!isExportSupported()) {
      setExportError("Video export is not supported in this browser. Please use Chrome or Edge.");
      return;
    }

    setIsExporting(true);
    setExportProgress(0);
    setExportStatus("Starting export...");
    setExportError(null);

    try {
      const timeline = toJSON();
      const blob = await exportTimeline(timeline, {
        width: 1920,
        height: 1080,
        fps: 30,
        videoBitrate: 5_000_000,
        onProgress: setExportProgress,
        onStatus: setExportStatus,
      });

      // Download the file
      const filename = `export-${Date.now()}.mp4`;
      downloadBlob(blob, filename);
      
      setExportStatus("Export complete!");
    } catch (error) {
      console.error("Export failed:", error);
      setExportError(error.message || "Export failed");
      setExportStatus("");
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, toJSON]);

  const handleExportAudio = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    setExportProgress(0);
    setExportStatus("Starting audio export...");
    setExportError(null);

    try {
      const timeline = toJSON();
      const blob = await exportAudio(timeline, {
        sampleRate: 48000,
        channels: 2,
        onProgress: setExportProgress,
        onStatus: setExportStatus,
      });

      // Download the file
      const filename = `export-${Date.now()}.wav`;
      downloadBlob(blob, filename);
      
      setExportStatus("Audio export complete!");
    } catch (error) {
      console.error("Audio export failed:", error);
      setExportError(error.message || "Audio export failed");
      setExportStatus("");
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, toJSON]);
  
  const handleSave = async () => {
    try {
      await saveToDB();
    } catch (e) {
      console.error('Save failed:', e);
    }
  }

  const handleRevert = useCallback(async () => {
    if (!show?._id) return;
    const targetTimelineId = timelineId || timelineIdParam;
    if (!targetTimelineId) return;

    await openModal(
      <ConfirmModal
        title="Revert Timeline"
        message="This will discard all unsaved changes and reload the last saved version. Continue?"
        type="warning"
        buttons={[
          {
            label: "Revert",
            type: "primary",
            onClick: async () => {
              setLoadError(null);
              await loadFromDB(show._id, targetTimelineId);
            },
          },
        ]}
      />
    );
  }, [openModal, show?._id, timelineId, timelineIdParam, loadFromDB]);

  useEffect(() => {
    if (!show?._id || !timelineIdParam) return;
    loadFromDB(show._id, timelineIdParam).catch((e) => setLoadError(e.message));
  }, [show?._id, timelineIdParam]);

  if (isLoading) {
    return <Loader status="Loading timeline..." />
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-destructive">Failed to load timeline: {loadError}</p>
        <Button type="secondary" onClick={() => navigate('/show/timelines')}>Back to Timelines</Button>
      </div>
    )
  }

  if (sketchEditorProps) {
    return (
      <DragPreviewProvider>
        <div className='flex flex-col w-full h-full relative'>
          <div className="flex-1 overflow-hidden">
            <SketchEditor
              sketchId={sketchEditorProps.sketchId}
              initialImageUrl={sketchEditorProps.initialImageUrl}
              onionImages={sketchEditorProps.onionImages}
              onSaveComplete={handleSketchSaveComplete}
              onClose={closeSketchEditor}
            />
          </div>
        </div>
      </DragPreviewProvider>
    );
  }

  return (
    <DragPreviewProvider>
    <div className='flex flex-col w-full h-full relative'>
      <div className='flex items-center justify-between px-4 h-20 border-b'>
          <div className='flex items-center gap-2'>
              <Link to="/show/timelines" className='text-secondary-foreground hover:text-white cursor-pointer px-2 flex justify-center items-center rounded-md'>
                  <FaChevronLeft className='w-4 h-4' />
              </Link>
              {isRenaming ? (
                <input
                  ref={renameInputRef}
                  className='text-2xl font-bold text-secondary-foreground bg-transparent border-b border-primary outline-none'
                  defaultValue={timelineName || 'Untitled'}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (val && val !== timelineName) setTimelineName(val);
                    setIsRenaming(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.target.blur();
                    if (e.key === 'Escape') { setIsRenaming(false); }
                  }}
                />
              ) : (
                <div className='flex items-center gap-2'>
                  <h1 className='text-2xl font-bold text-secondary-foreground select-none'>{timelineName || 'Untitled'}</h1>
                  <button onClick={() => setIsRenaming(true)} className='text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer'>
                    <FiEdit2 className='w-3 h-3' />
                  </button>
                </div>
              )}
          </div>
          <div className='flex items-center gap-2'>
            {!isSaving && lastSavedAt ? (
              <span className='text-xs text-secondary-foreground/50 select-none'>
                Last saved {dayjs().diff(dayjs(lastSavedAt), 'hour') < 1 ? dayjs(lastSavedAt).format('h:mm A') : dayjs(lastSavedAt).format('MMM D, YYYY h:mm A')}
              </span>
            ) : <div className='flex items-center gap-2 text-xs text-secondary-foreground/50'>
              <LuLoaderCircle className='w-4 h-4 animate-spin' />
              <span>Saving...</span>
              </div>}
            {/* <Button icon={<CiSaveUp1 className='w-4 h-4' />} type='secondary' onClick={handleSave} disabled={isSaving || !isDirty}>
              {isSaving ? 'Saving...' : isDirty ? 'Save' : 'Saved'}
            </Button> */}
            <UserRole role='editor'>
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <div className='flex items-center gap-2 bg-secondary text-secondary-foreground border shadow hover:bg-secondary/80 p-2 text-xs rounded-lg'>
                    <FiMoreHorizontal className='w-4 h-4' />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem disabled={!isDirty} onClick={handleSave} className="text-xs">
                    <CiSaveUp1 className='w-4 h-4' />
                    Save
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={!isDirty} onClick={handleRevert} className="text-xs">
                    <FiRotateCcw className='w-4 h-4' />
                    Revert
                  </DropdownMenuItem>
                  {segmentId && episodeId && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={async () => {
                          if (isDirty) {
                            const result = await openModal(
                              <ConfirmModal
                                title="Unsaved Changes"
                                message="You have unsaved changes. What would you like to do?"
                                type="warning"
                                buttons={[
                                  { label: "Discard Changes", type: "secondary", onClick: () => {} },
                                  { label: "Save Changes", type: "primary", onClick: saveToDB },
                                ]}
                              />
                            );
                            if (result === undefined) return;
                            useEditor.setState({ isDirty: false });
                          }
                          navigate(`/show/episodes/${episodeId}/${segmentId}/script`);
                        }}
                        className="text-xs"
                      >
                        <LuLetterText className='w-4 h-4' />
                        Go to Script
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={async () => {
                          if (isDirty) {
                            const result = await openModal(
                              <ConfirmModal
                                title="Unsaved Changes"
                                message="You have unsaved changes. What would you like to do?"
                                type="warning"
                                buttons={[
                                  { label: "Discard Changes", type: "secondary", onClick: () => {} },
                                  { label: "Save Changes", type: "primary", onClick: saveToDB },
                                ]}
                              />
                            );
                            if (result === undefined) return;
                            useEditor.setState({ isDirty: false });
                          }
                          navigate(`/show/episodes/${episodeId}/${segmentId}/storyboard`);
                        }}
                        className="text-xs"
                      >
                        <CiViewTimeline className='w-4 h-4' />
                        Go to Storyboard
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportVideo} className="text-xs">
                    <LuVideo className='w-4 h-4' />
                    Export Video (MP4)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAudio} className="text-xs">
                    <LuAudioLines className='w-4 h-4' />
                    Export Audio (WAV)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </UserRole>
          </div>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-col flex-1 overflow-hidden min-w-0">
          <div className='flex flex-1 overflow-hidden min-h-0'>
            <ResizablePanelGroup direction="horizontal" className="!h-auto flex-shrink-0 overflow-hidden">
              <ResizablePanel defaultSize={300} minSize={200} maxSize={800}>
                <Tabs value={editorTab} onValueChange={setEditorTab} className="flex flex-col flex-1 overflow-hidden h-full">
                  <TabsList className="w-full shrink-0 rounded-none h-10">
                    <TabsTrigger value="assets" className="text-xs">Assets</TabsTrigger>
                    <TabsTrigger value="properties" className="text-xs">Properties</TabsTrigger>
                  </TabsList>
                  <TabsContent value="assets" className="flex flex-col flex-1 overflow-hidden">
                    <TimelineAssets />
                  </TabsContent>
                  <TabsContent value="properties" className="flex flex-col flex-1 overflow-hidden">
                    <TimelineElementEditor />
                  </TabsContent>
                </Tabs>
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel className="flex-1 overflow-hidden flex flex-col"  >
                  <div className="flex-1 overflow-hidden">
                    <PreviewPane />
                  </div>
                  
                  <ControlBar
                    generationsOpen={generationsOpen}
                    onToggleGenerations={() => setGenerationsOpen((v) => !v)}
                    commentsOpen={commentsOpen}
                    onToggleComments={() => {
                      setCommentsOpen((v) => {
                        if (v) setHighlightCommentId(null);
                        return !v;
                      });
                    }}
                  />

                  <div className="flex overflow-hidden">
                    <Timeline comments={timelineComments} onCommentMarkerClick={handleCommentMarkerClick} />
                    <AudioMeter width={60} />
                  </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
         
          {/* <ResizablePanelGroup direction="horizontal" className="!h-auto flex-shrink-0 overflow-hidden">
            <ResizablePanel defaultSize={300} minSize={200} maxSize={800}>
              <TimelineAssets />
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel >
              
            </ResizablePanel>
          </ResizablePanelGroup> */}
        </div>
        {generationsOpen && (
          <StoryboardGenerations preview={selectedClipPreview} />
        )}
        {commentsOpen && (
          <TimelineComments
            timelineId={effectiveTimelineId}
            comments={timelineComments}
            loading={commentsLoading}
            fetchComments={fetchComments}
            addComment={addComment}
            updateComment={updateComment}
            highlightCommentId={highlightCommentId}
          />
        )}
      </div>
      {isExporting && <Loader status={exportStatus} showProgress={true} progress={exportProgress*100} />}
    </div>
    </DragPreviewProvider>
  );
}